import { Suspense, lazy } from 'react'
import { RouterProvider } from '@tanstack/react-router'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import * as Sentry from '@sentry/react'
import { router } from './router'
import { queryClient, persistOptions } from './lib/query'
import { useIsRestoring } from '@tanstack/react-query'
import { Spinner } from "@/components/ui/spinner"
import { ThemeProvider } from './components/shared/theme-provider'
import { Toaster } from "@/components/ui/sonner"

// Lazy load devtools only in development
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((mod) => ({
        default: mod.ReactQueryDevtools,
      }))
    )
  : () => null

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />} showDialog>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={persistOptions}
        >
          <AppContent />
          {import.meta.env.DEV && (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          )}
        </PersistQueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

function AppContent() {
  const isRestoring = useIsRestoring()

  if (isRestoring) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-4">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
    </>
  )
}

function SentryFallback() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-semibold">Something went wrong</h1>
      <p className="text-muted-foreground">
        Our team has been notified. Please refresh the page to try again.
      </p>
      <button
        className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
        onClick={() => window.location.reload()}
      >
        Refresh page
      </button>
    </div>
  )
}

export default App
